import { pipe, Either } from "fp";

import { Remote, Cache, Reference } from "~/Data";
import { Request as GenericRequest } from "~/Request";
import { State } from "~/State";

export type Store<A> = {
  readonly latest: Remote<Cache.Cache<A>>;
  readonly cache: Cache.Cache<A>;
};

export type Request<A> = GenericRequest.Fn<Cache.Cache<A>>;

export type When<A, B> = {
  readonly idle?: (cache: Cache.Cache<A>) => B;
  readonly loading: (cache: Cache.Cache<A>) => B;
  readonly error: (error: Error, cache: Cache.Cache<A>) => B;
  readonly success: (cache: Cache.Cache<A>) => B;
};

export const isEmpty = <A>(store: Store<A>): boolean =>
  pipe(store, toCache, Cache.isEmpty);

export const equals = <A>(a: Store<A>) => (b: Store<A>): boolean =>
  Cache.equals(toCache(a))(toCache(b)) &&
  Remote.equals(Cache.equals)(a.latest)(b.latest);

export const initial = <A>(): Store<A> => ({
  latest: Remote.loading(),
  cache: Cache.empty(),
});

export const requested = <A>(store: Store<A>): Store<A> => ({
  latest: Remote.loading(),
  cache: toCache(store),
});

export const received = <A>(received?: Either.ErrorOr<Cache.Cache<A>>) => (
  store: Store<A>
): Store<A> => ({
  latest: Remote.received(received ?? Either.right(Cache.empty())),
  cache: toCache(store),
});

export const receivedError = <A>(
  error: Error
): ((store: Store<A>) => Store<A>) => received<A>(Either.left(error));

export const when = <A, B>({
  loading,
  idle = loading,
  error,
  success,
}: When<A, B>) => (store: Store<A>): B =>
  pipe(
    store.latest,
    Remote.when({
      idle: () => idle(store.cache),
      loading: () => loading(store.cache),
      error: (e) => error(e, store.cache),
      success: () => success(toCache(store)),
    })
  );

export const updated = <A>({ latest, cache }: Store<A>): Store<A> => ({
  latest: Remote.map(Cache.updated)(latest),
  cache: Cache.updated(cache),
});

export const toCache = <A>({ latest, cache }: Store<A>): Cache.Cache<A> =>
  pipe(
    latest,
    Remote.getOrElse(() => Cache.empty(cache.lastUpdate)),
    Cache.concat(cache)
  );

export const toLatest = <A>({ latest }: Store<A>): Cache.Cache<A> => {
  return pipe(
    latest,
    Remote.getOrElse(() => Cache.empty())
  );
};

export const useSelector = <A>(select: State.Select<Store<A>>): Store<A> =>
  State.useSelector({ select, equals });

export namespace Filter {
  export type Filter<A, B> = Cache.Filter.Filter<A, B>;
  export const by = <A, B>(filter: Filter<A, B>) => ({
    latest,
    cache,
  }: Store<A>): Store<B> => ({
    latest: Remote.map(Cache.Filter.by(filter))(latest),
    cache: Cache.Filter.by(filter)(cache),
  });
}

export namespace Get {
  export const fromReference = <A, B>(get: Cache.Get.Get<A, B>) => (
    store: Store<A>
  ): B =>
    pipe(
      toCache(store),
      Cache.concat(store.cache),
      Cache.Get.fromReference(get)
    );

  export const fromFilter = <A, B>(filter: Filter.Filter<A, B>) => (
    store: Store<A>
  ): B | undefined => pipe(toCache(store), Cache.Get.fromFilter(filter));
}

export namespace Update {
  export type Update<A> = Cache.Update.Update<A>;

  export const removeFromReference = <A>(
    reference: Partial<Reference.Reference>
  ) => (store: Store<A>): Store<A> =>
    pipe(
      store,
      fromReference({ ...reference, found: (): A | undefined => undefined })
    );

  export const fromReference = <A>(update: Update<A>) => ({
    latest,
    cache,
  }: Store<A>): Store<A> => ({
    cache: Cache.Update.fromReference(update)(cache),
    latest: Remote.map(Cache.Update.fromReference(update))(latest),
  });
}
